import { useMemo, useRef, useState, useEffect } from "react";
import { Link, NavLink, Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useIntakeQueueCount } from "@/lib/useIntakeQueueCount";
import ThemeToggle from "@/components/ThemeToggle";
import TrialBanner from "@/components/TrialBanner";
import BugReportButton from "@/components/BugReportButton";
import DemoPackageSwitch, { IS_LOCAL_DEMO, isDemoPathVisible, useDemoPackage } from "@/components/demo/DemoPackageSwitch";
import {
  LayoutDashboard,
  Layers,
  Thermometer,
  RotateCcw,
  FileCheck,
  CalendarCheck,
  CreditCard,
  Users,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronRight,
  ExternalLink,
  Leaf,
  ShieldCheck,
  FileText,
  ClipboardList,
  ListChecks,
  CalendarDays,
  BookOpen,
  Wrench,
  UsersRound,
  PanelLeftClose,
  PanelLeftOpen,
  Package,
  Boxes,
  Briefcase,
  AlertTriangle,
  MoreHorizontal,
  Pin,
  PinOff,
  MessageCircleQuestion,
  Smartphone,
  Inbox,
  ChevronDown,
  Receipt,
  Globe,
  Megaphone,
  PlusCircle,
  Banknote,
  MapPin,
  BarChart3,
} from "lucide-react";

type NavItem = { to: string; icon: any; label: string; end?: boolean };

const NAV_SECTIONS = [
  {
    label: "Composting",
    feature: "composting",
    items: [
      { to: "/app", icon: LayoutDashboard, label: "Dashboard", end: true },
      { to: "/app/windrows", icon: Layers, label: "Windrows and Piles" },
      { to: "/app/log-temp", icon: Thermometer, label: "Log Temperature" },
      { to: "/app/log-turn", icon: RotateCcw, label: "Log Turn" },
      { to: "/app/reports", icon: FileCheck, label: "Reports" },
    ],
  },
  {
    label: "Operations",
    feature: "operations",
    items: [
      { to: "/app/ops/intake-queue", icon: Inbox, label: "Intake Queue" },
      { to: "/app/ops/bols", icon: FileText, label: "BOLs" },
      { to: "/app/ops/jobs", icon: Briefcase, label: "Jobs" },
      // { to: "/app/ops/cods", icon: ClipboardList, label: "CODs" }, // Hidden — reactivate when needed
      { to: "/app/ops/work-orders", icon: Wrench, label: "Work Orders" },
      { to: "/app/ops/tasks", icon: ListChecks, label: "Task Board" },
      { to: "/app/ops/clients", icon: UsersRound, label: "Clients" },
      { to: "/app/ops/products", icon: Package, label: "Products & Ingredients" },
      { to: "/app/ops/inventory", icon: Boxes, label: "Inventory" },
    ],
    moreItems: [
      { to: "/app/ops/calendar", icon: CalendarDays, label: "Calendar" },
      { to: "/app/ops/incidents", icon: AlertTriangle, label: "Incidents" },
      { to: "/app/ops/resources", icon: BookOpen, label: "Resources" },
    ],
  },
  {
    label: "Admin",
    feature: "admin",
    items: [
      { to: "/app/team", icon: Users, label: "Team" },
      { to: "/app/feedback", icon: MessageCircleQuestion, label: "Feedback" },
      { to: "/app/billing", icon: CreditCard, label: "Billing" },
      { to: "/app/settings", icon: Settings, label: "Settings" },
    ],
  },
  {
    label: "Platform",
    feature: "platform_admin",
    items: [
      { to: "/app/operations", icon: ShieldCheck, label: "Operations Management" },
      { to: "/app/demo-requests", icon: CalendarCheck, label: "Demo Requests" },
      { to: "/app/waitlist", icon: Users, label: "Waitlist" },
    ],
  },
];

const BREADCRUMB_LABELS: Record<string, string> = {
  bols: "BOLs", cods: "CODs", "work-orders": "Work Orders", "task-board": "Task Board", "intake-queue": "Intake Queue",
  recipes: "Recipes", ingredients: "Ingredients",
  enterprise: "Enterprise sample",
  orders: "Order ledger",
  "new-order": "New quote",
  receivables: "Receivables",
  collect: "Collect payment",
  contacts: "Contacts",
  leads: "Lead map",
  pages: "Landing pages",
  campaigns: "Campaigns",
};
function getBreadcrumb(pathname: string) {
  const segments = pathname.replace("/app", "").split("/").filter(Boolean);
  if (segments.length === 0) return "Dashboard";
  return segments
    .map((s) => BREADCRUMB_LABELS[s] || s.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()))
    .join(" / ");
}

function MoreMenu({
  items,
  collapsed: sidebarCollapsed,
  onNavClick,
  onPin,
}: {
  items: NavItem[];
  collapsed: boolean;
  onNavClick: () => void;
  onPin: (to: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isMoreActive = items.some((item) => location.pathname.startsWith(item.to));

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        title={sidebarCollapsed ? "More" : undefined}
        className={`flex items-center gap-2.5 px-3 py-1.5 rounded-lg text-[12px] font-medium transition-all duration-150 w-full ${
          sidebarCollapsed ? "justify-center" : ""
        } ${
          isMoreActive
            ? "bg-sidebar-active text-white shadow-sm shadow-forest-900/30"
            : "text-sidebar-foreground/70 hover:text-white hover:bg-sidebar-hover"
        }`}
      >
        <MoreHorizontal size={15} strokeWidth={1.75} className="shrink-0" />
        {!sidebarCollapsed && "More"}
      </button>
      {open && (
        <div
          className={`absolute z-[60] bg-sidebar border border-sidebar-border rounded-lg shadow-xl py-1 min-w-[180px] ${
            sidebarCollapsed ? "left-full top-0 ml-2" : "left-0 top-full mt-1"
          }`}
          aria-label="More navigation links"
        >
          <div className="px-2 pb-1 pt-1 text-[10px] uppercase tracking-wide text-sidebar-foreground/40">
            Customize
          </div>
          {items.map(({ to, icon: Icon, label, end }) => (
            <div key={to} className="flex items-center group">
              <NavLink
                to={to}
                end={end}
                onClick={() => {
                  setOpen(false);
                  onNavClick();
                }}
                className={({ isActive }) =>
                  `flex-1 flex items-center gap-2.5 px-3 py-1.5 text-[12px] font-medium transition-all duration-150 ${
                    isActive
                      ? "bg-sidebar-active text-white"
                      : "text-sidebar-foreground/70 hover:text-white hover:bg-sidebar-hover"
                  }`
                }
              >
                <Icon size={14} strokeWidth={1.75} className="shrink-0" />
                {label}
              </NavLink>
              <button
                type="button"
                className="mr-1.5 inline-flex items-center justify-center w-6 h-6 rounded-md text-sidebar-foreground/50 hover:text-white hover:bg-sidebar-hover opacity-0 translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-150"
                title="Pin to sidebar"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onPin(to);
                }}
              >
                <Pin size={12} strokeWidth={1.75} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const [demoPackage, selectDemoPackage] = useDemoPackage();
  const homePath = IS_LOCAL_DEMO ? "/app/windrows" : "/app";

  const CRITICAL_ROUTES = useMemo(
    () =>
      new Set<string>([
        "/app",
        "/app/ops",
        "/app/team",
        "/app/billing",
        "/app/settings",
      ]),
    []
  );

  const [pinnedPaths, setPinnedPaths] = useState<string[]>(() => {
    if (typeof window === "undefined") return [];
    try {
      const raw = window.localStorage.getItem("cd_sidebar_pins");
      return raw ? (JSON.parse(raw) as string[]) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    if (IS_LOCAL_DEMO) return;
    try {
      window.localStorage.setItem("cd_sidebar_pins", JSON.stringify(pinnedPaths));
    } catch {
      // ignore
    }
  }, [pinnedPaths]);

  const pinnableSet = useMemo(() => {
    const set = new Set<string>();
    if (IS_LOCAL_DEMO) return set;
    for (const section of NAV_SECTIONS as any[]) {
      const baseItems = section.items as NavItem[];
      const moreItems = section.moreItems as NavItem[] | undefined;
      for (const item of baseItems) {
        if (!CRITICAL_ROUTES.has(item.to)) set.add(item.to);
      }
      if (moreItems) {
        for (const item of moreItems) {
          if (!CRITICAL_ROUTES.has(item.to)) set.add(item.to);
        }
      }
    }
    return set;
  }, [CRITICAL_ROUTES]);

  const handlePin = (to: string) => {
    setPinnedPaths((prev) => (prev.includes(to) ? prev : [...prev, to]));
  };

  const handleUnpin = (to: string) => {
    setPinnedPaths((prev) => prev.filter((p) => p !== to));
  };

  const visibleSections = useMemo(() => {
    const features = user?.features || ["composting"];
    if (IS_LOCAL_DEMO) {
      const onMarketingSample =
        location.pathname.startsWith("/app/enterprise/pages") ||
        location.pathname.startsWith("/app/enterprise/campaigns") ||
        location.pathname.startsWith("/app/enterprise/leads");
      const tracking = {
        label: "Compost tracking",
        defaultOpen: demoPackage !== "enterprise",
        items: [
          { to: "/app/windrows", icon: Layers, label: "Windrows & Piles" },
          { to: "/app/recipes", icon: BookOpen, label: "Recipes" },
          { to: "/app/ingredients", icon: Leaf, label: "Ingredients" },
          { to: "/app/log-temp", icon: Thermometer, label: "Log Temperature" },
          { to: "/app/log-turn", icon: RotateCcw, label: "Log Turn" },
        ],
      };
      const operations = {
        label: "Daily operations",
        defaultOpen: false,
        items: [
          { to: "/app/ops/bols", icon: FileText, label: "BOLs" },
          { to: "/app/ops/jobs", icon: Briefcase, label: "Jobs" },
          { to: "/app/ops/clients", icon: UsersRound, label: "Clients" },
          { to: "/app/ops/products", icon: Package, label: "Products" },
          { to: "/app/ops/inventory", icon: Boxes, label: "Inventory" },
          { to: "/app/ops/incidents", icon: AlertTriangle, label: "Incidents" },
          { to: "/app/ops/work-orders", icon: Wrench, label: "Work Orders" },
        ],
      };
      const sales = {
        label: "Sales & collection",
        sample: true,
        defaultOpen: !onMarketingSample,
        items: [
          { to: "/app/enterprise", icon: BarChart3, label: "Sales graph", end: true },
          { to: "/app/enterprise/orders", icon: FileText, label: "Order ledger", end: true },
          { to: "/app/enterprise/new-order", icon: PlusCircle, label: "New quote", end: true },
          { to: "/app/enterprise/receivables", icon: Receipt, label: "Receivables", end: true },
          { to: "/app/enterprise/collect", icon: Banknote, label: "Collect payment", end: true },
          { to: "/app/enterprise/contacts", icon: UsersRound, label: "Contacts", end: true },
        ],
      };
      const marketing = {
        label: "Marketing",
        sample: true,
        defaultOpen: false,
        items: [
          { to: "/app/enterprise/leads", icon: MapPin, label: "Lead map", end: true },
          { to: "/app/enterprise/pages", icon: Globe, label: "Landing pages", end: true },
          { to: "/app/enterprise/campaigns", icon: Megaphone, label: "Campaigns", end: true },
        ],
      };
      const team = {
        label: "Team & setup",
        defaultOpen: false,
        items: [
          { to: "/app/team", icon: Users, label: "Team" },
          { to: "/app/settings", icon: Settings, label: "Settings" },
        ],
      };
      if (demoPackage === "starter") return [tracking];
      if (demoPackage === "professional") return [tracking, operations];
      return [tracking, operations, sales, marketing, team];
    }
    return NAV_SECTIONS.filter((s) => features.includes(s.feature));
  }, [user?.features, demoPackage, location.pathname]);
  const [openDemoSections, setOpenDemoSections] = useState<Record<string, boolean>>({});
  const demoSectionsCollapsible = IS_LOCAL_DEMO && demoPackage === "enterprise";

  const hasOperations = useMemo(
    () => (user?.features || []).includes("operations"),
    [user?.features]
  );
  const intakeCount = useIntakeQueueCount(hasOperations && !IS_LOCAL_DEMO);

  return (
    <div className="h-screen bg-background flex overflow-hidden">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:bg-card focus:p-3 focus:rounded-lg">Skip to content</a>
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar — fixed height, own scroll */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 bg-sidebar flex flex-col transition-all duration-300 ease-out lg:static lg:z-auto shrink-0 ${
          collapsed ? "w-[68px]" : "w-[240px]"
        } ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
      >
        {/* Logo */}
        <div className="h-12 flex items-center justify-between px-4 border-b border-sidebar-border shrink-0">
          <Link to={homePath} className="flex items-center gap-2 overflow-hidden">
            <div className="w-7 h-7 rounded-lg bg-sidebar-active flex items-center justify-center shrink-0">
              <Leaf size={14} className="text-white" />
            </div>
            {!collapsed && (
              <span className="text-[14px] font-extrabold text-white tracking-tight whitespace-nowrap">
                Compost<span className="text-forest-400">Developer</span><span className="text-earth-400">.com</span>
              </span>
            )}
          </Link>
          <button
            className="lg:hidden p-1.5 text-sidebar-foreground/60 hover:text-white rounded-md"
            onClick={() => setSidebarOpen(false)}
          >
            <X size={16} />
          </button>
        </div>

        {IS_LOCAL_DEMO && <DemoPackageSwitch plan={demoPackage} onChange={selectDemoPackage} collapsed={collapsed} />}

        {/* Navigation — scrollable */}
        <nav aria-label="Main navigation" className="flex-1 py-3 px-2 overflow-y-auto min-h-0">
          {visibleSections.map((section, si) => {
            const demoSection = section as { label: string; sample?: boolean; defaultOpen?: boolean; items: NavItem[] };
            const sectionActive = demoSection.items.some((item) =>
              location.pathname === item.to || (item.to !== "/app/enterprise" && location.pathname.startsWith(item.to + "/"))
            );
            const expanded =
              !demoSectionsCollapsible ||
              collapsed ||
              (openDemoSections[section.label] ?? (demoSection.defaultOpen !== false || sectionActive));
            return (
            <div key={section.label} className={si > 0 ? "mt-4" : ""}>
              {!collapsed && demoSectionsCollapsible ? (
                <button
                  type="button"
                  aria-expanded={expanded}
                  onClick={() =>
                    setOpenDemoSections((prev) => ({
                      ...prev,
                      [section.label]: !(prev[section.label] ?? (demoSection.defaultOpen !== false || sectionActive)),
                    }))
                  }
                  className="mb-1 flex w-full items-center justify-between gap-2 rounded-md px-3 py-1 text-left hover:bg-sidebar-hover"
                >
                  <span className="flex min-w-0 items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-sidebar-foreground/40">
                    <span className="truncate">{section.label}</span>
                    {demoSection.sample && (
                      <span className="rounded bg-earth-500/25 px-1 py-px text-[8px] font-bold tracking-wide text-earth-300">Sample</span>
                    )}
                  </span>
                  <ChevronDown size={12} className={`shrink-0 text-sidebar-foreground/40 transition-transform ${expanded ? "" : "-rotate-90"}`} />
                </button>
              ) : !collapsed ? (
                <p className="text-[10px] uppercase tracking-wider font-semibold px-3 mb-1.5 text-sidebar-foreground/40">
                  {section.label}
                </p>
              ) : null}
              {collapsed && si > 0 && (
                <div className="mx-3 mb-2 border-t border-sidebar-border" />
              )}
              <div className={`space-y-px ${expanded ? "" : "hidden"}`}>
                {(() => {
                  const baseItems = section.items as NavItem[];
                  const moreItems = (section as any).moreItems as NavItem[] | undefined;
                  const pinnedForSection =
                    moreItems?.filter((item) => pinnedPaths.includes(item.to)) ?? [];
                  const merged: NavItem[] = [...baseItems];
                  if (pinnedForSection.length) {
                    for (const item of pinnedForSection) {
                      if (!merged.some((i) => i.to === item.to)) {
                        merged.push(item);
                      }
                    }
                  }
                  return merged.map(({ to, icon: Icon, label, end }) => {
                    const isActive =
                      end || to === "/app/enterprise"
                        ? location.pathname === to
                        : location.pathname === to || location.pathname.startsWith(`${to}/`);
                    return (
                    // Each item in the main list
                    <NavLink
                      key={to}
                      to={to}
                      end={end || to === "/app/enterprise"}
                      onClick={() => setSidebarOpen(false)}
                      title={collapsed ? label : undefined}
                      className={`group flex items-center gap-2.5 px-3 py-1.5 rounded-lg text-[12px] font-medium transition-all duration-150 ${
                          collapsed ? "justify-center" : ""
                        } ${
                          isActive
                            ? "bg-sidebar-active text-white shadow-sm shadow-forest-900/30"
                            : "text-sidebar-foreground/70 hover:text-white hover:bg-sidebar-hover"
                        }`}
                    >
                      <span className="relative shrink-0 flex items-center">
                        <Icon size={15} strokeWidth={1.75} />
                        {collapsed && to === "/app/ops/intake-queue" && intakeCount > 0 && (
                          <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-red-500 animate-pulse ring-2 ring-sidebar" />
                        )}
                      </span>
                      {!collapsed && (
                        <span className="flex-1 flex items-center justify-between gap-1">
                          <span className="flex items-center gap-1.5">
                            <span>{label}</span>
                            {to === "/app/ops/intake-queue" && intakeCount > 0 && (
                              <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none animate-pulse">
                                {intakeCount > 99 ? "99+" : intakeCount}
                              </span>
                            )}
                          </span>
                          {pinnableSet.has(to) && (
                            <button
                              type="button"
                              className="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-md text-sidebar-foreground/40 hover:text-white hover:bg-sidebar-hover opacity-0 translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-150"
                              title={pinnedPaths.includes(to) ? "Unpin from sidebar" : "Pin to sidebar"}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (pinnedPaths.includes(to)) {
                                  handleUnpin(to);
                                } else {
                                  handlePin(to);
                                }
                              }}
                            >
                              {pinnedPaths.includes(to) ? (
                                <PinOff size={12} strokeWidth={1.75} />
                              ) : (
                                <Pin size={12} strokeWidth={1.75} />
                              )}
                            </button>
                          )}
                        </span>
                      )}
                    </NavLink>
                    );
                  });
                })()}
                {(() => {
                  const moreItems = (section as any).moreItems as NavItem[] | undefined;
                  if (!moreItems) return null;
                  const remaining = moreItems.filter((item) => !pinnedPaths.includes(item.to));
                  if (!remaining.length) return null;
                  return (
                  <MoreMenu
                      items={remaining}
                      collapsed={collapsed}
                      onNavClick={() => setSidebarOpen(false)}
                      onPin={handlePin}
                    />
                  );
                })()}
              </div>
            </div>
            );
          })}
        </nav>

        {/* Direct operator app entry */}
        <div className="px-2 pb-2 shrink-0">
          {!collapsed && (
            <p className="text-[10px] uppercase tracking-wider font-semibold px-3 mb-1.5 text-sidebar-foreground/40">
              Field App
            </p>
          )}
          <NavLink
            to="/app/operator"
            onClick={() => setSidebarOpen(false)}
            title={collapsed ? "Operator App" : undefined}
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-3 py-2 rounded-lg text-[12px] font-semibold transition-all duration-150 ${
                collapsed ? "justify-center" : ""
              } ${
                isActive
                  ? "bg-sidebar-active text-white shadow-sm shadow-forest-900/30"
                  : "text-sidebar-foreground/80 hover:text-white hover:bg-sidebar-hover"
              }`
            }
          >
            <Smartphone size={15} strokeWidth={1.8} className="shrink-0" />
            {!collapsed && <span>Operator App</span>}
          </NavLink>
        </div>

        {/* Collapse toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="hidden lg:flex items-center justify-center gap-2 mx-2 mb-1 p-1.5 text-sidebar-foreground/40 hover:text-white hover:bg-sidebar-hover rounded-lg transition-colors text-[11px]"
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? <PanelLeftOpen size={14} /> : <PanelLeftClose size={14} />}
          {!collapsed && <span>Collapse</span>}
        </button>

        {/* User */}
        <div className="p-2 border-t border-sidebar-border shrink-0">
          {collapsed ? (
            <div className="flex flex-col items-center gap-1.5">
              <div className="w-7 h-7 rounded-full bg-sidebar-active/80 flex items-center justify-center text-white text-[10px] font-bold ring-2 ring-sidebar-active/30">
                {user?.name?.[0] || user?.email?.[0] || "U"}
              </div>
              <button
                onClick={logout}
                className="p-1.5 text-sidebar-foreground/50 hover:text-white hover:bg-sidebar-hover rounded-lg transition-colors"
                title="Sign out"
              >
                <LogOut size={13} />
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2.5 mb-1.5 px-1">
                <div className="w-7 h-7 rounded-full bg-sidebar-active/80 flex items-center justify-center text-white text-[10px] font-bold ring-2 ring-sidebar-active/30 shrink-0">
                  {user?.name?.[0] || user?.email?.[0] || "U"}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[12px] font-medium text-white/90 truncate">{user?.name || "User"}</p>
                  <p className="text-[10px] text-sidebar-foreground/50 truncate">{user?.email}</p>
                </div>
              </div>
              <button
                onClick={logout}
                className="flex items-center gap-2 w-full px-3 py-1.5 text-[11px] text-sidebar-foreground/50 hover:text-white hover:bg-sidebar-hover rounded-lg transition-colors"
              >
                <LogOut size={12} />
                Sign out
              </button>
            </>
          )}
        </div>
      </aside>

      {/* Main content — scrolls independently */}
      <div className="flex-1 flex flex-col min-w-0 h-screen">
        {!IS_LOCAL_DEMO && <TrialBanner />}
        <header className="h-11 bg-card/80 backdrop-blur-md border-b border-border flex items-center justify-between px-4 shrink-0 z-30">
          <div className="flex items-center gap-2">
            <button
              className="lg:hidden p-1.5 text-muted-foreground hover:text-foreground rounded-lg hover:bg-secondary transition-colors"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu size={18} />
            </button>
            <div className="flex items-center gap-1.5 text-xs">
              <Link to={homePath} className="text-muted-foreground hover:text-foreground transition-colors">
                {IS_LOCAL_DEMO ? "Windrows" : "Dashboard"}
              </Link>
              {location.pathname !== homePath && (
                <>
                  <ChevronRight size={12} className="text-muted-foreground/50" />
                  <span className="text-foreground font-medium">{getBreadcrumb(location.pathname)}</span>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <ThemeToggle />
            <Link
              to="/"
              className="hidden sm:flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded-md hover:bg-secondary"
            >
              <ExternalLink size={11} />
              Site
            </Link>
          </div>
        </header>

        <main id="main-content" tabIndex={-1} className="flex-1 p-3 lg:p-4 overflow-auto">
          {IS_LOCAL_DEMO && !isDemoPathVisible(location.pathname, demoPackage)
            ? <Navigate to="/app/windrows" replace />
            : <Outlet />}
        </main>
      </div>

      {!IS_LOCAL_DEMO && <BugReportButton />}
    </div>
  );
}
